import type { MetaFunction } from "@remix-run/node"

const DEFAULT_TITLE = "A Family Experience Company"
const DEFAULT_DESCRIPTION =
  "CAMP is a Family Experience Company. Designed to inspire and engage families, CAMP combines play, media and merchandise to create an enriching and fun experience for kids and grown ups."
export const DEFAULT_OG_IMAGE = "https://camp.com/images/og-image.jpeg"

export const buildMetaTags = (
  matches: Parameters<MetaFunction>[0]["matches"],
  location: Parameters<MetaFunction>[0]["location"],
  {
    title,
    description,
    imageUrl,
    hidden,
    multiImageUrls,
    videoThumbnailUrls,
    noCanonical
  }: {
    title?: string | null | undefined
    description?: string | null | undefined
    imageUrl?: string | null | undefined
    hidden?: boolean
    multiImageUrls?: { desktop: string; mobile: string }[]
    videoThumbnailUrls?: string[]
    noCanonical?: boolean
  } = {}
) => {
  const tags = []

  // Title
  tags.push({
    title: `${title ?? DEFAULT_TITLE} | Camp`
  })
  tags.push({ property: "og:title", content: title ?? DEFAULT_TITLE })

  // Description
  tags.push({
    name: "description",
    content: description ?? DEFAULT_DESCRIPTION
  })
  tags.push({
    property: "og:description",
    content: description ?? DEFAULT_DESCRIPTION
  })

  // Image
  tags.push({ property: "og:image", content: imageUrl ?? DEFAULT_OG_IMAGE })

  tags.push({ property: "og:site_name", content: "CAMP" })

  const rootData = (matches as any).find((m: any) => m.id == "root")?.data

  if (!noCanonical) {
    tags.push({
      tagName: "link",
      rel: "canonical",
      href:
        location.pathname == "/"
          ? rootData.config.frontendBase
          : [rootData.config.frontendBase, location.pathname].join("")
    })
  }

  tags.push({
    name: "viewport",
    content: "width=device-width, initial-scale=1.0"
  })

  if (hidden) {
    tags.push({
      name: "robots",
      content: "noindex,nofollow"
    })
  }

  if (multiImageUrls) {
    multiImageUrls.forEach(({ desktop, mobile }) => {
      // 4K+ Displays (2160px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: desktop + "?fm=avif&w=2160&q=75",
        as: "image",
        media: "(min-width: 1681px)"
      })
      // Large Desktop (1680px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: desktop + "?fm=avif&w=1680&q=75",
        as: "image",
        media: "(min-width: 1201px) and (max-width: 1680px)"
      })
      // Desktop (1200px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: desktop + "?fm=avif&w=1200&q=75",
        as: "image",
        media: "(min-width: 961px) and (max-width: 1200px)"
      })
      // Small Desktop (960px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: desktop + "?fm=avif&w=960&q=75",
        as: "image",
        media: "(min-width: 721px) and (max-width: 960px)"
      })

      // Use mobile URL if provided, otherwise fallback to desktop
      const smallScreenUrl = mobile ?? desktop

      // Tablet (720px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: smallScreenUrl + "?fm=avif&w=720&q=75",
        as: "image",
        media: "(min-width: 481px) and (max-width: 720px)"
      })
      // Mobile (480px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: smallScreenUrl + "?fm=avif&w=480&q=75",
        as: "image",
        media: "(min-width: 241px) and (max-width: 480px)"
      })
      // Small Mobile (240px)
      tags.push({
        tagName: "link",
        rel: "preload",
        href: smallScreenUrl + "?fm=avif&w=240&q=75",
        as: "image",
        media: "(max-width: 240px)"
      })
    })
  }

  if (videoThumbnailUrls) {
    videoThumbnailUrls.forEach(url => {
      tags.push({
        tagName: "link",
        rel: "preload",
        href: url,
        as: "image"
      })
    })
  }

  return tags
}
